import template from "./create-invoice-modal.html";
import "./create-invoice-modal.less";

import {
	BUSINESS_CUSTOMERS,
	BUSINESS_CUSTOMER_TRANSACTIONS
} from "routes";

import {
	sum, 
	sumBy 
} from "lodash";
import swal from "sweetalert2";

// resolveType = {
// 		business: () => ctrl.business,
// 		customerEntities: () => ctrl.customerEntities,
//		reportValues: () => ctrl.getReportValues(true),
//		currentInvoices: () => ctrl.invoices,
//		deductionsFeature: () => ctrl.deductionsFeature,
// 		save: () => saveNewInvoice
// };

class CreateInvoiceModalController {
	constructor(
		$logger,
		toastr,
		api,
		appState,
		loadingService,
		entityManager,
		$uibModal
	) {
		this.$logger = $logger;
		this.toastr = toastr;
		this.api = api;
		this.appState = appState;
		this.loadingService = loadingService;
		this.entityManager = entityManager;
		this.$uibModal = $uibModal;
	}

	$onInit() {
		this.selectedSubtotal = 0;
		this.subTotal = 0;
		this.purchased = 0;
		this.referenceNumber = undefined;

		// default selected business to the resolved default
		this.selectedBusiness = this.resolve.businesses?.[0];

		const businessEntityType = this.appState.forms.selected.Agency.Types.find(t => t.Businesses.Code === "SUPPORTED");
		this.businessEntityLabel = businessEntityType.Name;

		if(this.resolve.customerEntities.length === 1) {
			this.selectedCustomer = { ...this.resolve.customerEntities[0] };
			this.loadCustomer();
		}
	}

	loadCustomer = async() => {
		try {
			const loadingCustomer = this.api.get(BUSINESS_CUSTOMERS(this.appState.forms.selected.Id, this.selectedBusiness.Business.Id, this.selectedCustomer.Id));

			this.customers = await this.loadingService.watch(loadingCustomer);

			this.getTotalForCustomer(this.selectedCustomer);

			if(this.customers.length === 0) {
				await this.createCustomer();
				this.transactions = [];

				return;
			}
		}
		catch(err) {
			this.$logger.exception(err, "customer load error", { selectedCustomer: this.selectedCustomer });

			this.toastr.error("There was an error loading the selected customer");

			return;
		}

		await this.loadTransactions();
	};

	loadTransactions = async() => {
		try {
			const loadingTransactions = this.api.get(BUSINESS_CUSTOMER_TRANSACTIONS(this.appState.forms.selected.Id, this.selectedBusiness.Business.Id, this.customers[0].Id, { status: "Pending" }));

			this.transactions = await this.loadingService.watch(loadingTransactions);

			this.transactions = this.transactions.filter(t => t.Invoices === undefined || t.Invoices.length === 0);

			this.defaultSelection();
		}
		catch(err) {
			this.$logger.exception(err, "transactions load error", { businessEntityId: this.selectedBusiness?.Id, selectedCustomer: this.selectedCustomer });

			this.toastr.error("There was an error loading transactions for the selected customer");

			return;
		}
	}

	createCustomer = async() => {
		const newCustomer = await this.loadingService.watch(
			this.api.post(
				BUSINESS_CUSTOMERS(this.appState.forms.selected.Id, this.selectedBusiness.Business.Id), 
				{
					Name: this.selectedCustomer.Name,
					Entities: [{ Id: this.selectedCustomer.Id }]
				}
			)
		);

		this.customers = [newCustomer];
	};

	handleCancel = () => {
		this.modalInstance.dismiss();
	};

	handleCreate = async() => {
		const selected = this.transactions
			.filter(t => t.isSelected)
			.map(t => ({ Id: t.Id }));

		const payload = { 
			Transactions: selected, 
			Amount: this.purchased, 
			ReferenceNumber: this.referenceNumber 
		};

		const isValid = await this.validate(payload);
		if(isValid) {
			const resp = await this.resolve.save(this.selectedBusiness.Business.Id, this.customers[0].Id, payload);

			this.modalInstance.close({ resp, customer: this.customers[0] });
		}
	};

	handleSelection = () => {
		const selected = this.transactions.filter(t => t.isSelected);
		this.selectedSubtotal = sumBy(selected, s => s.SubTotal ?? 0);

		this.subTotal = this.purchased - this.selectedSubtotal;

		this.selectAll = selected.length === this.transactions.length;
	}

	handlePurchasedChanged = () => {
		this.subTotal = this.purchased - this.selectedSubtotal;
	}

	getTotalForCustomer = (customerEntity) => {
		const entityReportValues = this.resolve.reportValues.filter(v => 
			v.EntityId !== undefined
			&& this.entityManager.findFriendlyByPrimaryId(this.resolve.customerEntities, v.EntityId)?.Id === customerEntity.Id
		);

		const quantityFieldId = this.resolve.deductionsFeature.Fields.find(f => f.Properties.Type === "QUANTITY").Field.Id;
		const priceFieldId = this.resolve.deductionsFeature.Fields.find(f => f.Properties.Type === "PRICE").Field.Id;

		let entityReportDetailIds;
		if(entityReportValues.length > 1 && (entityReportValues[0].DetailId !== undefined || entityReportValues[0].DetailLinkKey !== undefined)) {
			entityReportDetailIds = entityReportValues.map(v => v.DetailId ?? v.DetailLinkKey);
		}
		else {
			const detailIds = this.resolve.reportValues
				.filter(v => v.DetailId !== undefined || v.DetailLinkKey !== undefined)
				.reduce((acc, next) => {
					const nextId = next.DetailId ?? next.DetailLinkKey;
					acc[nextId] = nextId;

					return acc;
				}, {});

			entityReportDetailIds = Object.values(detailIds);
		}

		const totalSold = entityReportDetailIds.reduce((acc, next) => {
			const relatedDetailValues = this.resolve.reportValues.filter(v => v.DetailId === next || v.DetailLinkKey === next);

			const quantityFieldValue = relatedDetailValues.find(v => v.FieldId === quantityFieldId);
			const priceFieldValue = relatedDetailValues.find(v => v.FieldId === priceFieldId);

			const detailTotal = (quantityFieldValue?.Number ?? 0) * (priceFieldValue?.Number ?? 0);

			acc += detailTotal;

			return acc;
		}, 0);

		const totalCurrentInvoiced = (this.resolve.currentInvoices ?? []).map(i => i.Due?.Credit ?? 0);
		
		this.purchased = totalSold - sum(totalCurrentInvoiced);

		this.handlePurchasedChanged();
		
		return;
	}

	validate = async(payload) => {
		const validationStrings = [];
		
		if(!payload.Transactions?.length && (payload.Amount === undefined || payload.Amount === 0)) {
			validationStrings.push("A new invoice must have at least one transaction or a purchased amount.");
		}
	
		if(validationStrings.length > 0) {
			const errorList = `<ul><li>${validationStrings.join("</li><li>")}</li></ul>`;
				
			swal.fire({
				title: "Invalid values",
				html: 
						`<p>Please correct the following errors</p>
						${errorList}
						`,
				showCloseButton: true,
				type: "warning"
			});
				
			return false;
		}

		if(this.subTotal < 0) {
			const confirm = await swal.fire({
				title: "Excessive transaction value",
				html: 
						`<p>The total of your transactions exceeds the funds available from purchases.</p>
						<p>Are you sure you want to continue?</p>`,
				showCancelButton: true,
				confirmButtonColor: "#8CC541",
				confirmButtonText: "Yes, continue",
				type: "warning",
				width: "400px",
				reverseButtons: true
			});

			if(confirm.dismiss) {
				return false;
			} 
		}
	
		return true;
	};

	newTransaction = async() => {
		const update = await this.$uibModal.open({
			component: "business-customer-new-transaction-modal",
			size: "smmd",
			backdrop: "static",
			resolve: {
				businessId: () => this.selectedBusiness.Business.Id,
				save: () => (payload) => this.saveNewTransaction(payload)
			}
		}).result;

		update.isSelected = true;

		const matchindex = this.transactions.findIndex(c => c.Id === update.Id);
		if(matchindex !== -1) {
			this.transactions[matchindex] = update;
		}
		else {
			this.transactions.push(update);
		}	

		return update;
	};

	saveNewTransaction = async(params) => {
		const Transaction = params.item === undefined ?
				{
					Product: {
						Code: params.product.code
					},
					Quantity: params.product.quantity
				} :
				{
					Item: params.item.name,
					Price: params.item.price
				};
	
		Transaction.Fees = params.fees;
		const txnPayload = Transaction;
	
		try {
			return this.api.post(BUSINESS_CUSTOMER_TRANSACTIONS(this.appState.forms.selected.Id, this.selectedBusiness.Business.Id, this.customers[0].Id), txnPayload);
		}
		catch(err) {
			this.$logger.exception(err, "error saving new customer transaction");
			this.toastr.error("There was an error saving the new transaction");
		}
	};

	defaultSelection = () => {
		this.selectAll = false;

		const sortedTransactions = [...this.transactions].sort((a, b) => ((new Date(a.Created) < (new Date(b.Created)) ? -1 : 1)));
		let aggregateSelected = 0;

		sortedTransactions.forEach(t => {
			if(aggregateSelected + t.SubTotal < this.purchased) {
				t.isSelected = true;
				aggregateSelected += t.SubTotal;
				this.handleSelection();
			}
		});

		const hasUnselectedItems = sortedTransactions.some(t => t.isSelected !== true);

		this.selectAll = sortedTransactions.length > 0 && !hasUnselectedItems;
	}

	selectAllTransactions = () => {
		if(this.transactions.length < 1) {
			this.selectAll = false;
		} 
		else {
			this.transactions.forEach(t => {
				t.isSelected = this.selectAll;
			});
			this.handleSelection();
		}
	};
}

export default angular
	.module("vesl.components.create-invoice-modal", [])
	.component("createInvoiceModal", {
		controller: CreateInvoiceModalController,
		template,
		bindings: {
			close: "&",
			dismiss: "&",
			modalInstance: "<",
			resolve: "<"
		}
	}).name;
